import { useState } from "react";
import { Pencil, Check, X, KeyRound } from "lucide-react";
import { toast } from "sonner";

import { api } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../ui/dialog";
import { Label } from "../ui/label";

export default function ProfileCard() {
    const { user, refresh } = useAuth();
    const [editing, setEditing] = useState(false);
    const [name, setName] = useState(user?.name || "");
    const [saving, setSaving] = useState(false);

    const [pwOpen, setPwOpen] = useState(false);
    const [cur, setCur] = useState("");
    const [next, setNext] = useState("");
    const [confirm, setConfirm] = useState("");
    const [pwBusy, setPwBusy] = useState(false);

    const saveName = async () => {
        const trimmed = name.trim();
        if (!trimmed || trimmed === user?.name) {
            setEditing(false);
            setName(user?.name || "");
            return;
        }
        setSaving(true);
        try {
            const { data } = await api.patch("/auth/me", { name: trimmed });
            refresh?.(data);
            toast.success("Name updated");
            setEditing(false);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't update name");
        } finally {
            setSaving(false);
        }
    };

    const submitPw = async (e) => {
        e.preventDefault();
        if (next.length < 6) return toast.error("New password must be at least 6 characters");
        if (next !== confirm) return toast.error("New passwords don't match");
        setPwBusy(true);
        try {
            await api.post("/auth/change-password", { current_password: cur, new_password: next });
            toast.success("Password changed");
            setPwOpen(false);
            setCur(""); setNext(""); setConfirm("");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't change password");
        } finally {
            setPwBusy(false);
        }
    };

    return (
        <div className="rounded-3xl border border-stone-200/80 dark:border-stone-800 bg-white/70 dark:bg-[#242322]/70 p-6" data-testid="settings-profile">
            <div className="text-[11px] tracking-[0.2em] uppercase font-bold text-stone-400">Profile</div>
            <div className="mt-3 flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-terracotta/10 flex items-center justify-center font-serif text-2xl text-terracotta shrink-0">
                    {user?.name?.[0]?.toUpperCase() || "?"}
                </div>
                <div className="flex-1 min-w-0">
                    {editing ? (
                        <div className="flex items-center gap-2">
                            <Input
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                className="rounded-2xl"
                                data-testid="profile-name-input"
                                autoFocus
                            />
                            <Button size="icon" variant="ghost" onClick={saveName} disabled={saving} className="rounded-full" data-testid="profile-save">
                                <Check size={16} className="text-sage" />
                            </Button>
                            <Button size="icon" variant="ghost" onClick={() => { setEditing(false); setName(user?.name || ""); }} className="rounded-full" data-testid="profile-cancel">
                                <X size={16} />
                            </Button>
                        </div>
                    ) : (
                        <div className="flex items-center gap-2">
                            <div className="font-medium truncate">{user?.name}</div>
                            <Button size="icon" variant="ghost" onClick={() => setEditing(true)} className="rounded-full text-stone-400 hover:text-terracotta" data-testid="profile-edit">
                                <Pencil size={14} strokeWidth={1.5} />
                            </Button>
                        </div>
                    )}
                    <div className="text-sm text-stone-500 mt-1 truncate">{user?.email}</div>
                </div>
            </div>
            <div className="mt-4">
                <Button variant="outline" onClick={() => setPwOpen(true)} className="rounded-full" data-testid="profile-change-password">
                    <KeyRound size={14} className="mr-2" /> Change password
                </Button>
            </div>

            <Dialog open={pwOpen} onOpenChange={setPwOpen}>
                <DialogContent className="rounded-3xl max-w-md" data-testid="change-password-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-serif text-2xl font-light tracking-tight">Change password</DialogTitle>
                        <DialogDescription className="text-sm text-stone-500">You'll stay signed in on this device.</DialogDescription>
                    </DialogHeader>
                    <form onSubmit={submitPw} className="space-y-4">
                        <div>
                            <Label className="text-[10px] tracking-[0.2em] uppercase font-bold text-stone-400">Current password</Label>
                            <Input type="password" value={cur} onChange={(e) => setCur(e.target.value)} className="mt-2 rounded-2xl" data-testid="cp-current" required />
                        </div>
                        <div>
                            <Label className="text-[10px] tracking-[0.2em] uppercase font-bold text-stone-400">New password</Label>
                            <Input type="password" value={next} onChange={(e) => setNext(e.target.value)} className="mt-2 rounded-2xl" data-testid="cp-new" required />
                        </div>
                        <div>
                            <Label className="text-[10px] tracking-[0.2em] uppercase font-bold text-stone-400">Confirm new</Label>
                            <Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className="mt-2 rounded-2xl" data-testid="cp-confirm" required />
                        </div>
                        <DialogFooter>
                            <Button type="submit" disabled={pwBusy} className="rounded-full bg-terracotta hover:bg-[#D05A40] text-white" data-testid="cp-submit">
                                {pwBusy ? "Updating…" : "Update password"}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
}
